import { useState, useEffect, useCallback } from 'react';
import fetchAPI from '../helper/axios';
import fetchAPICustom from '../helper/custom';
import { SolutionPageData, BenefitItem, DigitalFeature, soluhero } from '../types/solution';

// Cache storage
const cache = new Map<string, { data: any; timestamp: number }>();
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

const fetchWithCache = async (endpoint: string, isCustom = false,
  retries = 3,
  delay = 1000): Promise<any> => {
  try{
  const now = Date.now();
  const cached = cache.get(endpoint);
  
  if (cached && (now - cached.timestamp) < CACHE_DURATION) {
    return cached.data;
  }
  
  const data = isCustom ? await fetchAPICustom(endpoint) : await fetchAPI(endpoint);
  cache.set(endpoint, { data, timestamp: now });
  return data;
   } catch (err) {
    if (retries > 0) {
      console.warn(`Retrying ${endpoint}... (${retries} left)`);
      await new Promise((resolve) => setTimeout(resolve, delay));
      return fetchWithCache(endpoint,isCustom, retries - 1, delay);
    }
    throw err;
  }
};

// Progressive loading with priorities
const API_ENDPOINTS = {
  critical: [
    { endpoint: 'node/38', isCustom: false }, // Hero data
    { endpoint: 'media/40/edit', isCustom: false }, // Hero image
     { endpoint: 'media/135/edit', isCustom: false }, // Hero mobile
  ],
  important: [
    { endpoint: 'node/40', isCustom: false }, // About data
    { endpoint: 'media/49/edit', isCustom: false }, // About image
  ],
  secondary: [
    { endpoint: 'node/41', isCustom: false }, // Benefits data
    { endpoint: 'media/50/edit', isCustom: false }, // Benefit images
    { endpoint: 'media/51/edit', isCustom: false },
    { endpoint: 'media/52/edit', isCustom: false },
     { endpoint: 'media/138/edit', isCustom: false },
      { endpoint: 'media/139/edit', isCustom: false },
       { endpoint: 'media/140/edit', isCustom: false },
  ],
  // deferred: [
  //   { endpoint: 'node/43', isCustom: false }, // Features data
  //   { endpoint: 'media/70/edit', isCustom: false }, // Features image
  //   { endpoint: 'media/57/edit', isCustom: false }, // Digital features
  //   { endpoint: 'media/61/edit', isCustom: false },
  //   { endpoint: 'media/62/edit', isCustom: false },
  //   { endpoint: 'media/63/edit', isCustom: false },
  //   { endpoint: 'webslider.php?id=6', isCustom: true }, // Tab data
  
  // ]
};

export const useSolutionData = () => {
  const [data, setData] = useState<SolutionPageData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [retry, setRetry] = useState(0); // for retry trigger
  const [loadingProgress, setLoadingProgress] = useState<{
    critical: boolean;
    important: boolean;
    secondary: boolean;
   // deferred: boolean;
  }>({
    critical: true,
    important: false,
    secondary: false,
   // deferred: false,
  });

  const loadSection = useCallback(async (endpoints: Array<{endpoint: string, isCustom: boolean}>, section: keyof typeof loadingProgress) => {
    try {
      const results = await Promise.all(
        endpoints.map(({ endpoint, isCustom }) => fetchWithCache(endpoint, isCustom))
      );
      return results;
    } catch (err) {
      console.error(`Error loading ${section} section:`, err);
      throw err;
    }
  }, []);

  const updateData = useCallback((partialData: Partial<SolutionPageData>) => {
    setData(prev => prev ? { ...prev, ...partialData } : partialData as SolutionPageData);
  }, []);

  // Process tab data
  const processTabData = (tabData: any) => {
   // console.log('Processing tab data:', tabData);
    
    if (!tabData) {
      console.warn('No tab data received');
      return null;
    }
    
    // Handle different data structures
    let sliderImages = null;
    
    if (tabData.data?.slider_images) {
      sliderImages = tabData.data.slider_images;
    } else if (tabData.slider_images) {
      sliderImages = tabData.slider_images;
    } else if (Array.isArray(tabData)) {
      sliderImages = tabData;
    }
    
    if (!sliderImages || !Array.isArray(sliderImages)) {
      console.warn('No valid slider images found in tab data');
      return {
        id: 'default',
        title: 'Default Tabs',
        description: 'Default tab description',
        slider_images: []
      };
    }
    
    const processedImages = sliderImages.map((slide: any, index: number) => ({
      entity_id: slide.entity_id || `slide-${index}`,
      slider_title: slide.slider_title || `Slide ${index + 1} Title`,
      slider_desc: slide.slider_desc || `Slide ${index + 1} Description`,
      slider_img_url: slide.slider_img_url ? slide.slider_img_url.replace("public://", "") : '',
      button_title: slide.button_title || `Tab ${index + 1}`
    }));
    
    const processedData = {
      id: tabData.id || 'default',
      title: tabData.title || 'Default Tabs',
      description: tabData.description || 'Default tab description',
      slider_images: processedImages
    };
    
  //  console.log('Processed tab data:', processedData);
    return processedData;
  };

  useEffect(() => {
    const loadDataProgressive = async () => {
      try {
       // console.log('Starting solution data load...');
        
        // Load critical data first (Hero section)
      //  console.log('Loading critical data...');
        const criticalResults = await loadSection(API_ENDPOINTS.critical, 'critical');
     //   console.log('Critical results:', criticalResults);
         
        updateData({
          hero: {
            data: criticalResults[0],
            image: criticalResults[1],
             mobimage: criticalResults[2]
          }
        });
        setLoadingProgress(prev => ({ ...prev, critical: false, important: true }));

        // Load important data (About section)
      //  console.log('Loading important data...');
        const importantResults = await loadSection(API_ENDPOINTS.important, 'important');
      //  console.log('Important results:', importantResults);
        
        updateData({
          about: {
            data: importantResults[0],
            image: importantResults[1]
          }
        });
        setLoadingProgress(prev => ({ ...prev, important: false, secondary: true }));

        // Load secondary data (Benefits section)
       // console.log('Loading secondary data...');
        const secondaryResults = await loadSection(API_ENDPOINTS.secondary, 'secondary');
      //  console.log('Secondary results:', secondaryResults);
        
        const benefitItems: BenefitItem[] = [
          { image: secondaryResults[1], title: secondaryResults[0]?.field_title?.[0]?.value || '' },
          { image: secondaryResults[2], title: secondaryResults[0]?.field_title?.[1]?.value || '' },
          { image: secondaryResults[3], title: secondaryResults[0]?.field_title?.[2]?.value || '' },
          { image: secondaryResults[4], title: secondaryResults[0]?.field_title?.[3]?.value || '' },
          { image: secondaryResults[5], title: secondaryResults[0]?.field_title?.[4]?.value || '' },
          { image: secondaryResults[6], title: secondaryResults[0]?.field_title?.[5]?.value || '' }
        ];

        updateData({
          benefits: {
            data: secondaryResults[0],
            items: benefitItems
          }
        });
        setLoadingProgress(prev => ({ ...prev, secondary: false, deferred: true }));

        // Load deferred data (Features and Tabs)
        //console.log('Loading deferred data...');
     //   const deferredResults = await loadSection(API_ENDPOINTS.deferred, 'deferred');
      //  console.log('Deferred results:', deferredResults);
        
        // const digitalFeatures: DigitalFeature[] = [
        //   {
        //     image: deferredResults[2],
        //     title: deferredResults[0]?.field_title?.[0]?.value || '',
        //     description: deferredResults[0]?.field_sub_description?.[0]?.value || ''
        //   },
        //   {
        //     image: deferredResults[3],
        //     title: deferredResults[0]?.field_title?.[1]?.value || '',
        //     description: deferredResults[0]?.field_sub_description?.[1]?.value || ''
        //   },
        //   {
        //     image: deferredResults[4],
        //     title: deferredResults[0]?.field_title?.[2]?.value || '',
        //     description: deferredResults[0]?.field_sub_description?.[2]?.value || ''
        //   },
        //   {
        //     image: deferredResults[5],
        //     title: deferredResults[0]?.field_title?.[3]?.value || '',
        //     description: deferredResults[0]?.field_sub_description?.[3]?.value || ''
        //   }
        // ];

        // updateData({
        //   tabs: {
        //     data: processTabData(deferredResults[0]),
        //     //  groone: processTabData(deferredResults[1]),
        //     //   grocredit: processTabData(deferredResults[2]),

        //     features: {
        //       data: deferredResults[0],
        //       image: deferredResults[1],
        //       digitalFeatures
        //     }
        //   }
        // });
        setLoadingProgress(prev => ({ ...prev, deferred: false }));

        //console.log('Solution data loaded successfully');
        setLoading(false);
      } catch (err: any) {
        console.error('Error loading solution data:', err);
        setError(err.message || 'Failed to load solution data');
           // Retry after 3 seconds
        setTimeout(() => setRetry((prev) => prev + 1), 10000);
        setLoading(false);
      }
    };

    loadDataProgressive();
  }, [loadSection, updateData,retry]);

  return { 
    data, 
    loading, 
    error, 
    loadingProgress,
    retry: () => {
      setLoading(true);
      setError(null);
      setLoadingProgress({
        critical: true,
        important: false,
        secondary: false,
       // deferred: false,
      });
    }
  };
};
